import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { type SellerScoreResult, type SellerTier, SCORE_COMPONENT_MAX, TIER_CONFIG } from "@/lib/seller-score";

interface SellerTrustScoreProps {
  result: SellerScoreResult;
  size?: "sm" | "md" | "lg";
  showLabel?: boolean;
  className?: string;
}

function ScoreRing({ score, tier, size }: { score: number; tier: SellerTier; size: "sm" | "md" | "lg" }) {
  const dimensions = { sm: 40, md: 64, lg: 88 };
  const strokeWidths = { sm: 3, md: 4, lg: 5 };
  const fontSizes = { sm: "text-xs", md: "text-base", lg: "text-xl" };

  const dim = dimensions[size];
  const strokeWidth = strokeWidths[size];
  const radius = (dim - strokeWidth * 2) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = TIER_CONFIG[tier].color;

  return (
    <div className="relative" style={{ width: dim, height: dim }}>
      <svg width={dim} height={dim} className="-rotate-90">
        {/* Background ring */}
        <circle
          cx={dim / 2}
          cy={dim / 2}
          r={radius}
          fill="none"
          stroke="hsl(var(--muted))"
          strokeWidth={strokeWidth}
        />
        {/* Progress ring */}
        <circle
          cx={dim / 2}
          cy={dim / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-700 ease-out"
        />
      </svg>
      <div className={cn(
        "absolute inset-0 flex items-center justify-center font-display font-bold",
        fontSizes[size]
      )}>
        {score}
      </div>
    </div>
  );
}

function BreakdownTooltip({ result }: { result: SellerScoreResult }) {
  const { breakdown } = result;
  const items = [
    { label: "Account Age", value: breakdown.accountAge, max: SCORE_COMPONENT_MAX.accountAge },
    // Free claims count here — they are acquisitions — so the label says so.
    { label: "Downloads & Sales", value: breakdown.sales, max: SCORE_COMPONENT_MAX.sales },
    { label: "Rating", value: breakdown.rating, max: SCORE_COMPONENT_MAX.rating },
    { label: "Reviews", value: breakdown.reviews, max: SCORE_COMPONENT_MAX.reviews },
    { label: "Assets", value: breakdown.assets, max: SCORE_COMPONENT_MAX.assets },
    { label: "Followers", value: breakdown.followers, max: SCORE_COMPONENT_MAX.followers },
    ...(breakdown.commissions > 0
      ? [{ label: "Commissions", value: breakdown.commissions, max: SCORE_COMPONENT_MAX.commissions }]
      : []),
    ...(breakdown.founderBonus > 0
      ? [{ label: "Founder Bonus", value: breakdown.founderBonus, max: SCORE_COMPONENT_MAX.founderBonus }]
      : []),
  ];

  return (
    <div className="space-y-2 min-w-[180px]">
      <p className="font-semibold text-xs mb-2">Trust Score Breakdown</p>
      {items.map((item) => (
        <div key={item.label} className="flex items-center justify-between text-xs gap-3">
          <span className="text-muted-foreground">{item.label}</span>
          <span className="font-mono font-medium">
            {item.value}/{item.max}
          </span>
        </div>
      ))}
      {breakdown.refundPenalty < 0 && (
        <div className="flex items-center justify-between text-xs gap-3 text-destructive">
          <span>Refund Penalty</span>
          <span className="font-mono font-medium">{breakdown.refundPenalty}</span>
        </div>
      )}
      <div className="flex items-center justify-between text-xs gap-3 pt-1 border-t border-border/50 font-semibold">
        <span>Total</span>
        <span className="font-mono">{result.score}/100</span>
      </div>
    </div>
  );
}

export function SellerTrustScore({ result, size = "md", showLabel = true, className }: SellerTrustScoreProps) {
  const tierConfig = TIER_CONFIG[result.tier];

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={cn("flex flex-col items-center gap-1.5 cursor-default", className)}>
            <ScoreRing score={result.score} tier={result.tier} size={size} />
            {showLabel && (
              <Badge
                variant="secondary"
                className={cn("text-2xs font-semibold uppercase tracking-wider border-0", tierConfig.bgClass)}
              >
                {tierConfig.label}
              </Badge>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="p-3">
          <BreakdownTooltip result={result} />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
